import React, { useState } from 'react';
import type { DirectCompetitorData } from './types';

interface CompetitorCardProps {
  competitor: DirectCompetitorData;
}

const CompetitorCard: React.FC<CompetitorCardProps> = ({ competitor }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const keyOfferings = JSON.parse(competitor.key_offerings);
  const strengths = JSON.parse(competitor.strengths);
  const weaknesses = JSON.parse(competitor.weaknesses);

  return (
    <div className="bg-[#2a2a2a] rounded-lg border border-[#E0E0E021] overflow-hidden">
      {/* Header */}
      <div
        className="p-5 cursor-pointer hover:bg-[#333333] transition-colors"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-2">
              <h3 className="text-lg font-medium text-[#EE4423]">{competitor.competitor_name}</h3>
              <div className="flex items-center gap-1 px-2 py-0.5 bg-yellow-900/30 border border-yellow-700/50 rounded">
                <svg className="w-3 h-3 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                </svg>
                <span className="text-xs font-semibold text-yellow-300">{competitor.online_rating_avg}</span>
              </div>
            </div>

            <div className="flex items-center gap-2 text-[12px] text-gray-400 mb-2">
              <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                <path
                  fillRule="evenodd"
                  d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z"
                  clipRule="evenodd"
                />
              </svg>
              {competitor.address}
            </div>

            <p className="text-xs text-gray-300 font-normal">{competitor.perceived_market_position}</p>
          </div>

          <svg
            className={`w-5 h-5 text-orange-500 transition-transform flex-shrink-0 ${
              isExpanded ? 'rotate-180' : ''
            }`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </div>

      {/* Expanded Content */}
      {isExpanded && (
        <div className="px-5 pb-5 space-y-4 border-t border-[#4a4a4a]">
          {/* Key Offerings */}
          <div className="pt-4">
            <h4 className="text-md font-semibold text-white mb-2">Key Offerings</h4>
            <div className="flex flex-wrap gap-2">
              {keyOfferings.map((offering: string, index: number) => (
                <span
                  key={index}
                  className="px-3 py-1 bg-gray-900/30 text-gray-300 rounded text-xs border border-gray-700/50"
                >
                  {offering}
                </span>
              ))}
            </div>
          </div>

          {/* Strengths */}
          <div>
            <h4 className="text-sm font-medium text-gray-300 mb-2">Strengths</h4>
            <ul className="space-y-2">
              {strengths.map((strength: string, index: number) => (
                <li key={index} className="flex items-start gap-2 text-sm text-gray-300">
                  <span className="text-green-400 mt-1">✓</span>
                  <span>{strength}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Weaknesses */}
          <div>
            <h4 className="text-sm font-medium text-gray-300 mb-2">Weaknesses</h4>
            <ul className="space-y-2">
              {weaknesses.map((weakness: string, index: number) => (
                <li key={index} className="flex items-start gap-2 text-sm text-gray-300">
                  <span className="text-red-400 mt-1">✗</span>
                  <span>{weakness}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Opportunity */}
          <div className="bg-[#1a1a1a] rounded-lg p-4 border border-orange-700/50">
            <h4 className="text-sm font-semibold text-orange-400 mb-2">Opportunity</h4>
            <p className="text-sm text-gray-300 leading-relaxed">{competitor.opportunity}</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default CompetitorCard;
